import { useState, useEffect } from "react";

export const LANGUAGES = [
  { code: "mr", label: "मराठी" },
  { code: "hi", label: "हिंदी" },
  { code: "en", label: "English" },
  { code: "bn", label: "বাংলা" },
  { code: "te", label: "తెలుగు" },
  { code: "ta", label: "தமிழ்" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "ml", label: "മലയാളം" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "ur", label: "اردو" },
  { code: "or", label: "ଓଡ଼ିଆ" },
];

export function getLanguage() {
  return localStorage.getItem("grgpt_ui_language") || "en";
}

export function setLanguage(langCode) {
  localStorage.setItem("grgpt_ui_language", langCode);
  window.dispatchEvent(new CustomEvent("grgpt-language-change", { detail: langCode }));
}

export function useLanguage() {
  const [lang, setLangState] = useState(getLanguage);

  useEffect(() => {
    const handleLangChange = (e) => {
      const newLang = e.detail || getLanguage();
      setLangState(newLang);
    };
    window.addEventListener("grgpt-language-change", handleLangChange);
    return () => window.removeEventListener("grgpt-language-change", handleLangChange);
  }, []);

  return [lang, setLanguage];
}

export const TRANSLATIONS = {
  en: {
    navbar: {
      brandMain: "Government Assistant",
      brandSub: "Maharashtra Government Digital Communication",
    },
    login: {
      appTitle: "GRGPT & Tender Assistant",
      signIn: "Sign in",
      signUp: "Sign up",
      otpLogin: "OTP Login / Signup",
      welcomeBack: "Welcome back",
      createAccount: "Create your account",
      instantOtp: "Instant OTP Access",
      email: "Email",
      password: "Password",
      fullName: "Full name",
      mobileNumber: "Mobile Number",
      enterMobile: "Enter 10-digit number",
      sendOtp: "Send OTP Verification",
      enterOtp: "Enter 6-Digit OTP",
      verifyOtp: "Verify OTP Code",
      pleaseWait: "Please wait...",
      sending: "Sending...",
      verifying: "Verifying...",
      changeNumber: "Change number",
    },
    sidebar: {
      newChat: "New Chat",
      recent: "Recent",
      noChats: "No chats yet",
      signInHistory: "Sign in to see history",
      loading: "Loading...",
      untitled: "Untitled",
      credits: "Credits",
      upgrade: "Upgrade plan",
      myAccount: "My Account",
      logout: "Logout",
    },
    footer: {
      privacy: "Privacy",
      terms: "Terms & Condition",
      usage: "Policy",
    },
    authModal: {
      loginTitle: "Log in",
      signupTitle: "Sign up",
      loginSub: "Access GR Assistant",
      signupSub: "Create new account",
      cancel: "Cancel",
      noAccount: "Don't have an account?",
      hasAccount: "Already have an account?",
    },
    account: {
      myAccount: "My Account",
      editProfileTitle: "Edit Profile",
      user: "User",
      admin: "Admin",
      active: "Active",
      blocked: "Blocked",
      joinedOn: "Joined on",
      editProfile: "Edit Profile",
      credits: "Credits",
      balance: "Balance",
      totalPurchased: "Total Purchased",
      used: "Used",
      creditUsage: "Credit Usage",
      deptPref: "Department Preferences",
      noDept: "No department selected.",
      selectDept: "+ Select Department",
      changeDept: "Change Department",
      actions: "Actions",
      billingHistory: "Billing History",
      billingDesc: "History of all your purchases",
      upgradePlan: "Upgrade Plan",
      upgradeDesc: "Get more credits",
      loadingProfile: "Loading profile…",
      unableProfile: "Unable to load profile.",
      back: "Back",
      saveChanges: "Save Changes",
      saving: "Saving…",
      profileUpdated: "Profile updated successfully!",
      nameLabel: "Full Name",
      yourName: "Your name",
    },
    subscription: {
      title: "Subscription Plans",
      subtitle: "Choose the right plan for your government document research needs.",
      monthly: "Monthly",
      annual: "Annual",
      choosePlan: "Choose Plan",
      currentBalance: "Your current balance",
      mostPopular: "Most Popular",
    },
  },
  mr: {
    navbar: {
      brandMain: "शासन सहाय्यक",
      brandSub: "महाराष्ट्र शासन डिजिटल संवाद",
    },
    login: {
      appTitle: "GRGPT आणि निविदा सहाय्यक",
      signIn: "साइन इन",
      signUp: "नोंदणी करा",
      otpLogin: "OTP लॉगिन / नोंदणी",
      welcomeBack: "पुन्हा स्वागत आहे",
      createAccount: "तुमचे खाते तयार करा",
      instantOtp: "झटपट OTP प्रवेश",
      email: "ईमेल",
      password: "पासवर्ड",
      fullName: "पूर्ण नाव",
      mobileNumber: "मोबाईल नंबर",
      enterMobile: "१०-अंकी नंबर प्रविष्ट करा",
      sendOtp: "OTP पडताळणी पाठवा",
      enterOtp: "६-अंकी OTP प्रविष्ट करा",
      verifyOtp: "OTP कोड पडताळा",
      pleaseWait: "कृपया थांबा...",
      sending: "पाठवत आहे...",
      verifying: "पडताळणी करत आहे...",
      changeNumber: "नंबर बदला",
    },
    sidebar: {
      newChat: "नवीन चॅट",
      recent: "अलीकडील",
      noChats: "अजून चॅट नाहीत",
      signInHistory: "इतिहास पाहण्यासाठी साइन इन करा",
      loading: "लोड होत आहे...",
      untitled: "शीर्षक नाही",
      credits: "क्रेडिट्स",
      upgrade: "प्लॅन अपग्रेड करा",
      myAccount: "माझे खाते",
      logout: "लॉगआउट",
    },
    footer: {
      privacy: "गोपनीयता",
      terms: "अटी",
      usage: "वापर धोरण",
    },
    authModal: {
      loginTitle: "लॉगिन करा",
      signupTitle: "नोंदणी करा",
      loginSub: "GR Assistant मध्ये प्रवेश करा",
      signupSub: "नवीन खाते तयार करा",
      cancel: "रद्द करा",
      noAccount: "खाते नाही?",
      hasAccount: "आधीच खाते आहे?",
    },
    account: {
      myAccount: "माझे खाते",
      editProfileTitle: "प्रोफाईल संपादित करा",
      user: "वापरकर्ता",
      admin: "प्रशासक",
      active: "सक्रिय",
      blocked: "ब्लॉक केलेले",
      joinedOn: "सामील झाले",
      editProfile: "प्रोफाईल संपादित करा",
      credits: "क्रेडिट्स",
      balance: "शिल्लक",
      totalPurchased: "एकूण खरेदी",
      used: "वापरलेले",
      creditUsage: "क्रेडिट वापर",
      deptPref: "विभाग पसंती",
      noDept: "कोणताही विभाग निवडलेला नाही.",
      selectDept: "+ विभाग निवडा",
      changeDept: "विभाग बदला",
      actions: "कृती",
      billingHistory: "बिलिंग इतिहास",
      billingDesc: "तुमच्या सर्व खरेदीचा इतिहास",
      upgradePlan: "प्लॅन अपग्रेड करा",
      upgradeDesc: "अधिक क्रेडिट्स मिळवा",
      loadingProfile: "प्रोफाईल लोड होत आहे…",
      unableProfile: "प्रोफाईल लोड करण्यात अक्षम.",
      back: "मागे",
      saveChanges: "बदल जतन करा",
      saving: "जतन करत आहे…",
      profileUpdated: "प्रोफाईल यशस्वीरित्या अद्यतनित केले!",
      nameLabel: "पूर्ण नाव",
      yourName: "तुमचे नाव",
    },
    subscription: {
      title: "वर्गणी योजना",
      subtitle: "तुमच्या गरजेनुसार योग्य प्लॅन निवडा.",
      monthly: "मासिक",
      annual: "वार्षिक",
      choosePlan: "प्लॅन निवडा",
      currentBalance: "तुमचे सध्याचे शिल्लक",
      mostPopular: "सर्वात लोकप्रिय",
    },
  },
  hi: {
    navbar: {
      brandMain: "शासन सहायक",
      brandSub: "महाराष्ट्र शासन डिजिटल संवाद",
    },
    login: {
      appTitle: "GRGPT और निविदा सहायक",
      signIn: "साइन इन",
      signUp: "साइन अप",
      otpLogin: "OTP लॉगिन / साइन अप",
      welcomeBack: "वापसी पर स्वागत है",
      createAccount: "अपना खाता बनाएं",
      instantOtp: "त्वरित OTP पहुंच",
      email: "ईमेल",
      password: "पासवर्ड",
      fullName: "पूरा नाम",
      mobileNumber: "मोबाइल नंबर",
      enterMobile: "10 अंकों का नंबर दर्ज करें",
      sendOtp: "OTP सत्यापन भेजें",
      enterOtp: "6 अंकों का OTP दर्ज करें",
      verifyOtp: "OTP कोड सत्यापित करें",
      pleaseWait: "कृपया प्रतीक्षा करें...",
      sending: "भेजा जा रहा है...",
      verifying: "सत्यापित किया जा रहा है...",
      changeNumber: "नंबर बदलें",
    },
    sidebar: {
      newChat: "नई चैट",
      recent: "हाल ही में",
      noChats: "अभी तक कोई चैट नहीं",
      signInHistory: "इतिहास देखने के लिए साइन इन करें",
      loading: "लोड हो रहा है...",
      untitled: "बिना शीर्षक",
      credits: "क्रेडिट्स",
      upgrade: "प्लाएन अपग्रेड करें",
      myAccount: "मेरा अकाउंट",
      logout: "लॉगआउट",
    },
    footer: {
      privacy: "गोपनीयता",
      terms: "नियम",
      usage: "उपयोग नीति",
    },
    authModal: {
      loginTitle: "लॉगिन करें",
      signupTitle: "साइन अप करें",
      loginSub: "GR Assistant में प्रवेश करें",
      signupSub: "नया खाता बनाएं",
      cancel: "रद्द करें",
      noAccount: "खाता नहीं है?",
      hasAccount: "पहले से खाता है?",
    },
    account: {
      myAccount: "मेरा अकाउंट",
      editProfileTitle: "प्रोफ़ाइल संपादित करें",
      user: "उपयोगकर्ता",
      admin: "प्रशासक",
      active: "सक्रिय",
      blocked: "अवरुद्ध",
      joinedOn: "शामिल होने की तिथि",
      editProfile: "प्रोफ़ाइल संपादित करें",
      credits: "क्रेडिट्स",
      balance: "बैलेंस",
      totalPurchased: "कुल खरीदा गया",
      used: "उपयोग किया गया",
      creditUsage: "क्रेडिट उपयोग",
      deptPref: "विभाग प्राथमिकताएं",
      noDept: "कोई विभाग चुना नहीं गया।",
      selectDept: "+ विभाग चुनें",
      changeDept: "विभाग बदलें",
      actions: "कार्रवाइयां",
      billingHistory: "बिलिंग इतिहास",
      billingDesc: "आपकी सभी खरीदारी का इतिहास",
      upgradePlan: "प्लाएन अपग्रेड करें",
      upgradeDesc: "अधिक क्रेडिट्स प्राप्त करें",
      loadingProfile: "प्रोफ़ाइल लोड हो रही है…",
      unableProfile: "प्रोफ़ाइल लोड करने में असमर्थ।",
      back: "वापस",
      saveChanges: "बदलाव सहेजें",
      saving: "सहेजा जा रहा है…",
      profileUpdated: "प्रोफ़ाइल सफलतापूर्वक अद्यतनित की गई!",
      nameLabel: "पूरा नाम",
      yourName: "आपका नाम",
    },
    subscription: {
      title: "सदस्यता योजनाएं",
      subtitle: "अपनी आवश्यकताओं के अनुसार सही योजना चुनें।",
      monthly: "मासिक",
      annual: "वार्षिक",
      choosePlan: "योजना चुनें",
      currentBalance: "आपका वर्तमान बैलेंस",
      mostPopular: "सबसे लोकप्रिय",
    },
  },
  bn: {
    navbar: {
      brandMain: "সরকারি সহায়ক",
      brandSub: "মহারাষ্ট্র সরকার ডিজিটাল যোগাযোগ",
    },
    login: {
      appTitle: "GRGPT এবং দরপত্র সহায়ক",
      signIn: "সাইন ইন",
      signUp: "সাইন আপ",
      otpLogin: "OTP লগইন / সাইন আপ",
      welcomeBack: "স্বাগতম",
      createAccount: "আপনার অ্যাকাউন্ট তৈরি করুন",
      instantOtp: "তাত্ক্ষণিক OTP অ্যাক্সেস",
      email: "ইমেল",
      password: "পাসওয়ার্ড",
      fullName: "পূর্ণ নাম",
      mobileNumber: "মোবাইল নম্বর",
      enterMobile: "১০ সংখ্যার নম্বর লিখুন",
      sendOtp: "OTP যাচাইকরণ পাঠান",
      enterOtp: "৬ সংখ্যার OTP লিখুন",
      verifyOtp: "OTP কোড যাচাই করুন",
      pleaseWait: "অনুগ্রহ করে অপেক্ষা করুন...",
      sending: "পাঠানো হচ্ছে...",
      verifying: "যাচাই করা হচ্ছে...",
      changeNumber: "নম্বর পরিবর্তন করুন",
    },
    sidebar: {
      newChat: "নতুন চ্যাট",
      recent: "সাম্প্রতিক",
      noChats: "এখনও কোনও চ্যাট নেই",
      signInHistory: "ইতিহাস দেখতে সাইন ইন করুন",
      loading: "লোড হচ্ছে...",
      untitled: "শিরোনামহীন",
      credits: "ক্রেডিট",
      upgrade: "প্ল্যান আপগ্রেড করুন",
      myAccount: "আমার অ্যাকাউন্ট",
      logout: "লগ আউট",
    },
    footer: {
      privacy: "গোপনীয়তা",
      terms: "শর্তাবলী",
      usage: "নীতি",
    },
    authModal: {
      loginTitle: "লগ ইন করুন",
      signupTitle: "সাইন আপ করুন",
      loginSub: "GR সহায়কে প্রবেশ করুন",
      signupSub: "নতুন অ্যাকাউন্ট তৈরি করুন",
      cancel: "বাতিল করুন",
      noAccount: "অ্যাকাউন্ট নেই?",
      hasAccount: "ইতিমধ্যে একটি অ্যাকাউন্ট আছে?",
    },
    account: {
      myAccount: "আমার অ্যাকাউন্ট",
      editProfileTitle: "প্রোফাইল সম্পাদনা",
      user: "ব্যবহারকারী",
      admin: "অ্যাডমিন",
      active: "সক্রিয়",
      blocked: "ব্লক করা",
      joinedOn: "যোগদানের তারিখ",
      editProfile: "প্রোফাইল সম্পাদনা",
      credits: "ক্রেডিট",
      balance: "ব্যালেন্স",
      totalPurchased: "মোট কেনা হয়েছে",
      used: "ব্যবহৃত",
      creditUsage: "ক্রেডিট ব্যবহার",
      deptPref: "বিভাগ পছন্দ",
      noDept: "কোন বিভাগ নির্বাচিত হয়নি।",
      selectDept: "+ বিভাগ নির্বাচন করুন",
      changeDept: "বিভাগ পরিবর্তন করুন",
      actions: "পদক্ষেপ",
      billingHistory: "বিলিং ইতিহাস",
      billingDesc: "আপনার সমস্ত কেনাকাটার ইতিহাস",
      upgradePlan: "প্ল্যান আপগ্রেড করুন",
      upgradeDesc: "আরও ক্রেডিট পান",
      loadingProfile: "প্রোফাইল লোড হচ্ছে…",
      unableProfile: "প্রোফাইল লোড করতে অক্ষম।",
      back: "ফিরে যান",
      saveChanges: "পরিবর্তন সংরক্ষণ করুন",
      saving: "সংরক্ষণ করা হচ্ছে…",
      profileUpdated: "প্রোফাইল সফলভাবে আপডেট করা হয়েছে!",
      nameLabel: "পূর্ণ নাম",
      yourName: "আপনার নাম",
    },
    subscription: {
      title: "সাবস্ক্রিপশন প্ল্যান",
      subtitle: "আপনার জন্য সঠিক প্ল্যানটি চয়ন করুন।",
      monthly: "মাসিক",
      annual: "বার্ষিক",
      choosePlan: "প্ল্যান নির্বাচন করুন",
      currentBalance: "আপনার বর্তমান ব্যালেন্স",
      mostPopular: "সবচেয়ে জনপ্রিয়",
    },
  },
  te: {
    navbar: {
      brandMain: "ప్రభుత్వ సహాయకుడు",
      brandSub: "మహారాష్ట్ర ప్రభుత్వం డిజిటల్ కమ్యూనికేషన్",
    },
    login: {
      appTitle: "GRGPT & టెండర్ సహాయకుడు",
      signIn: "సైన్ ఇన్",
      signUp: "సైన్ అప్",
      otpLogin: "OTP లాగిన్ / సైన్ అప్",
      welcomeBack: "మళ్ళీ స్వాగతం",
      createAccount: "మీ ఖాతాను సృష్టించండి",
      instantOtp: "క్షణంలో OTP యాక్సెస్",
      email: "ఇమెయిల్",
      password: "పాస్ వర్డ్",
      fullName: "పూర్తి పేరు",
      mobileNumber: "మొబైల్ సంఖ్య",
      enterMobile: "10 అంకెల సంఖ్యను నమోదు చేయండి",
      sendOtp: "OTP ధృవీకరణను పంపండి",
      enterOtp: "6 అంకెల OTP నమోదు చేయండి",
      verifyOtp: "OTP కోడ్‌ను ధృవీకరించండి",
      pleaseWait: "దయచేసి వేచి ఉండండి...",
      sending: "పంపుతోంది...",
      verifying: "ధృవీకరిస్తోంది...",
      changeNumber: "సంఖ్య మార్చండి",
    },
    sidebar: {
      newChat: "కొత్త చాట్",
      recent: "ఇటీవలి",
      noChats: "ఇంకా చాట్‌లు లేవు",
      signInHistory: "చరిత్రను చూడటానికి సైన్ ఇన్ చేయండి",
      loading: "లోడ్ అవుతోంది...",
      untitled: "శీర్షిక లేదు",
      credits: "క్రెడిట్‌లు",
      upgrade: "ప్లాన్ అప్‌గ్రేడ్ చేయండి",
      myAccount: "నా ఖాతా",
      logout: "లాగ్ అవుట్",
    },
    footer: {
      privacy: "గోప్యత",
      terms: "నిబంధనలు",
      usage: "విధానం",
    },
    authModal: {
      loginTitle: "లాగిన్ చేయండి",
      signupTitle: "సైన్ అప్ చేయండి",
      loginSub: "GR అసిస్టెంట్‌ను యాక్సెస్ చేయండి",
      signupSub: "కొత్త ఖాతాను సృష్టించండి",
      cancel: "రద్దు చేయండి",
      noAccount: "ఖాతా లేదా?",
      hasAccount: "ఇప్పటికే ఖాతా ఉందా?",
    },
    account: {
      myAccount: "నా ఖాతా",
      editProfileTitle: "ప్రొఫైల్ సవరించండి",
      user: "వినియోగదారు",
      admin: "అడ్మిన్",
      active: "యాక్టివ్",
      blocked: "బ్లాక్ చేయబడింది",
      joinedOn: "చేరిన తేదీ",
      editProfile: "ప్రొఫైల్ సవరించండి",
      credits: "క్రెడిట్‌లు",
      balance: "బ్యాలెన్స్",
      totalPurchased: "మొత్తం కొనుగోలు చేసినవి",
      used: "ఉపయోగించినవి",
      creditUsage: "క్రెడిట్ వినియోగం",
      deptPref: "విభాగం ప్రాధాన్యతలు",
      noDept: "ఏ విభాగం ఎంచుకోబడలేదు.",
      selectDept: "+ విభాగం ఎంచుకోండి",
      changeDept: "విభాగం మార్చండి",
      actions: "చర్యలు",
      billingHistory: "బిల్లింగ్ చరిత్ర",
      billingDesc: "మీ కొనుగోళ్ల చరిత్ర",
      upgradePlan: "ప్లాన్ అప్‌గ్రేడ్ చేయండి",
      upgradeDesc: "మరిన్ని క్రెడిట్‌లు పొందండి",
      loadingProfile: "ప్రొఫైల్ లోడ్ అవుతోంది…",
      unableProfile: "ప్రొఫైల్ లోడ్ చేయడం సాధ్యపడలేదు.",
      back: "వెనుకకు",
      saveChanges: "మార్పులను సేవ్ చేయండి",
      saving: "సేవ్ చేస్తోంది…",
      profileUpdated: "ప్రొఫైల్ విజయవంతంగా నవీకరించబడింది!",
      nameLabel: "పూర్తి పేరు",
      yourName: "మీ పేరు",
    },
    subscription: {
      title: "సబ్‌స్క్రిప్షన్ ప్లాన్‌లు",
      subtitle: "మీ అవసరాలకు తగిన ప్లాన్‌ను ఎంచుకోండి.",
      monthly: "నెలవారీ",
      annual: "వార్షిక",
      choosePlan: "ప్లాన్ ఎంచుకోండి",
      currentBalance: "మీ ప్రస్తుత బ్యాలెన్స్",
      mostPopular: "అత్యంత ప్రజాదరణ పొందినవి",
    },
  },
  ta: {
    navbar: {
      brandMain: "அரசு உதவியாளர்",
      brandSub: "மகாராஷ்டிரா அரசு டிஜிட்டல் தொடர்பு",
    },
    login: {
      appTitle: "GRGPT & டெண்டர் உதவியாளர்",
      signIn: "உள்நுழைக",
      signUp: "பதிவு செய்க",
      otpLogin: "OTP உள்நுழைவு / பதிவு",
      welcomeBack: "மீண்டும் வருக",
      createAccount: "உங்கள் கணக்கை உருவாக்குங்கள்",
      instantOtp: "உடனடி OTP அணுகல்",
      email: "மின்னஞ்சல்",
      password: "கடவுச்சொல்",
      fullName: "முழு பெயர்",
      mobileNumber: "கைபேசி எண்",
      enterMobile: "10 இலக்க எண்ணை உள்ளிடவும்",
      sendOtp: "OTP சரிபார்ப்பை அனுப்பு",
      enterOtp: "6 இலக்க OTP ஐ உள்ளிடவும்",
      verifyOtp: "OTP குறியீட்டை சரிபார்க்கவும்",
      pleaseWait: "தயவுசெய்து காத்திருக்கவும்...",
      sending: "அனுப்பப்படுகிறது...",
      verifying: "சரிபார்க்கப்படுகிறது...",
      changeNumber: "எண்ணை மாற்றவும்",
    },
    sidebar: {
      newChat: "புதிய அரட்டை",
      recent: "சமீபத்திய",
      noChats: "இன்னும் அரட்டைகள் இல்லை",
      signInHistory: "வரலாற்றைக் காண உள்நுழையவும்",
      loading: "ஏற்றப்படுகிறது...",
      untitled: "தலைப்பிடப்படாதது",
      credits: "கிரெடிட்கள்",
      upgrade: "திட்டத்தை மேம்படுத்தவும்",
      myAccount: "என் கணக்கு",
      logout: "வெளியேறு",
    },
    footer: {
      privacy: "தனியுரிமை",
      terms: "விதிகள்",
      usage: "கொள்கை",
    },
    authModal: {
      loginTitle: "உள்நுழைக",
      signupTitle: "பதிவு செய்க",
      loginSub: "GR உதவியாளரை அணுகவும்",
      signupSub: "புதிய கணக்கை உருவாக்கவும்",
      cancel: "ரத்து செய்",
      noAccount: "கணக்கு இல்லையா?",
      hasAccount: "ஏற்கனவே கணக்கு உள்ளதா?",
    },
    account: {
      myAccount: "என் கணக்கு",
      editProfileTitle: "சுயவிவரத்தைத் திருத்து",
      user: "பயனர்",
      admin: "நிர்வாகி",
      active: "செயலில் உள்ளது",
      blocked: "முடக்கப்பட்டது",
      joinedOn: "இணைந்த தேதி",
      editProfile: "சுயவிவரத்தைத் திருத்து",
      credits: "கிரெடிட்கள்",
      balance: "மீதி",
      totalPurchased: "மொத்தம் வாங்கியது",
      used: "பயன்படுத்தப்பட்டது",
      creditUsage: "கிரெடிட் பயன்பாடு",
      deptPref: "துறை விருப்பங்கள்",
      noDept: "துறை எதுவும் தேர்ந்தெடுக்கப்படவில்லை.",
      selectDept: "+ துறையைத் தேர்ந்தெடுக்கவும்",
      changeDept: "துறையை மாற்றவும்",
      actions: "செயல்கள்",
      billingHistory: "பில்லிங் வரலாறு",
      billingDesc: "உங்கள் அனைத்து வாங்குதல்களின் வரலாறு",
      upgradePlan: "திட்டத்தை மேம்படுத்தவும்",
      upgradeDesc: "கூடுதல் கிரெடிட்களைப் பெறுங்கள்",
      loadingProfile: "சுயவிவரம் ஏற்றப்படுகிறது…",
      unableProfile: "சுயவிவரத்தை ஏற்ற முடியவில்லை.",
      back: "பின்னால்",
      saveChanges: "மாற்றங்களைச் சேமி",
      saving: "சேமிக்கப்படுகிறது…",
      profileUpdated: "சுயவிவரம் வெற்றிகரமாக புதுப்பிக்கப்பட்டது!",
      nameLabel: "முழு பெயர்",
      yourName: "உங்கள் பெயர்",
    },
    subscription: {
      title: "சந்தா திட்டங்கள்",
      subtitle: "உங்கள் தேவைகளுக்கு ஏற்ப சரியான திட்டத்தை தேர்வு செய்யவும்.",
      monthly: "மாதாந்திர",
      annual: "ஆண்டு",
      choosePlan: "திட்டத்தை தேர்வு செய்க",
      currentBalance: "உங்கள் தற்போதைய மீதி",
      mostPopular: "மிகவும் பிரபலமானது",
    },
  },
  gu: {
    navbar: {
      brandMain: "સરકારી મદદનીશ",
      brandSub: "મહારાષ્ટ્ર સરકાર ડિજિટલ સંચાર",
    },
    login: {
      appTitle: "GRGPT અને ટેન્ડર મદદનીશ",
      signIn: "સાઇન ઇન",
      signUp: "સાઇન અપ",
      otpLogin: "OTP સાઇન ઇન / સાઇન અપ",
      welcomeBack: "પાછા સ્વાગત છે",
      createAccount: "તમારું ખાતું બનાવો",
      instantOtp: "ઇન્સ્ટન્ટ OTP એક્સેસ",
      email: "ઇમેઇલ",
      password: "પાસવર્ડ",
      fullName: "પૂરું નામ",
      mobileNumber: "મોબાઇલ નંબર",
      enterMobile: "10 અંકનો નંબર દાખલ કરો",
      sendOtp: "OTP ચકાસણી મોકલો",
      enterOtp: "6 અંકનો OTP દાખલ કરો",
      verifyOtp: "OTP કોડ ચકાસો",
      pleaseWait: "કૃપા કરીને રાહ જુઓ...",
      sending: "મોકલી રહ્યું છે...",
      verifying: "ચકાસી રહ્યું છે...",
      changeNumber: "નંબર બદલો",
    },
    sidebar: {
      newChat: "નવી ચેટ",
      recent: "તાજેતરનું",
      noChats: "હજુ સુધી કોઈ ચેટ નથી",
      signInHistory: "ઇતિહાસ જોવા માટે સાઇન ઇન કરો",
      loading: "લોડ થઈ રહ્યું છે...",
      untitled: "શીર્ષક વગરનું",
      credits: "ક્રેડિટ્સ",
      upgrade: "પ્લાન અપગ્રેડ કરો",
      myAccount: "મારું એકાઉન્ટ",
      logout: "લોગ આઉટ",
    },
    footer: {
      privacy: "ગોપનીયતા",
      terms: "શરતો",
      usage: "નીતિ",
    },
    authModal: {
      loginTitle: "સાઇન ઇન કરો",
      signupTitle: "સાઇન અપ કરો",
      loginSub: "GR મદદનીશ ઍક્સેસ કરો",
      signupSub: "નવું ખાતું બનાવો",
      cancel: "રદ કરો",
      noAccount: "ખાતું નથી?",
      hasAccount: "પહેલેથી જ ખાતું છે?",
    },
    account: {
      myAccount: "મારું એકાઉન્ટ",
      editProfileTitle: "પ્રોફાઇલ સંપાદિત કરો",
      user: "ઉપયોગકર્તા",
      admin: "એડમિન",
      active: "સક્રિય",
      blocked: "બ્લોક કરેલ",
      joinedOn: "જોડાયા તારીખ",
      editProfile: "પ્રોફાઇલ સંપાદિત કરો",
      credits: "ક્રેડિટ્સ",
      balance: "બેલેન્સ",
      totalPurchased: "કુલ ખરીદેલ",
      used: "વપરાયેલ",
      creditUsage: "ક્રેડિટ વપરાશ",
      deptPref: "વિભાગ પસંદગીઓ",
      noDept: "કોઈ વિભાગ પસંદ કરેલ નથી.",
      selectDept: "+ વિભાગ પસંદ કરો",
      changeDept: "વિભાગ બદલો",
      actions: "ક્રિયાઓ",
      billingHistory: "બિલિંગ ઇતિહાસ",
      billingDesc: "તમારી બધી ખરીદીઓનો ઇતિહાસ",
      upgradePlan: "પ્લાન અપગ્રેડ કરો",
      upgradeDesc: "વધુ ક્રેડિટ્સ મેળવો",
      loadingProfile: "પ્રોફાઇલ લોડ થઈ રહી છે…",
      unableProfile: "પ્રોફાઇલ લોડ કરવામાં અસમર્થ.",
      back: "પાછા",
      saveChanges: "ફેરફારો સાચવો",
      saving: "સાચવી રહ્યું છે…",
      profileUpdated: "પ્રોફાઇલ સફળતાપૂર્વક અપડેટ થઈ!",
      nameLabel: "પૂરું નામ",
      yourName: "તમારું નામ",
    },
    subscription: {
      title: "સબ્સ્ક્રિપ્શન યોજનાઓ",
      subtitle: "તમારી જરૂરિયાતો અનુસાર યોગ્ય યોજના પસંદ કરો.",
      monthly: "માસિક",
      annual: "વાર્ષિક",
      choosePlan: "યોજના પસંદ કરો",
      currentBalance: "તમારું વર્તમાન બેલેન્સ",
      mostPopular: "સૌથી લોકપ્રિય",
    },
  },
  kn: {
    navbar: {
      brandMain: "ಸರ್ಕಾರಿ ಸಹಾಯಕ",
      brandSub: "ಮಹಾರಾಷ್ಟ್ರ ಸರ್ಕಾರ ಡಿಜಿಟಲ್ ಸಂವಹನ",
    },
    login: {
      appTitle: "GRGPT ಮತ್ತು ಟೆಂಡರ್ ಸಹಾಯಕ",
      signIn: "ಸೈನ್ ಇನ್",
      signUp: "ಸೈನ್ ಅಪ್",
      otpLogin: "OTP ಲಾಗಿನ್ / ಸೈನ್ ಅಪ್",
      welcomeBack: "ಮತ್ತೆ ಸುಸ್ವಾಗತ",
      createAccount: "ನಿಮ್ಮ ಖಾತೆಯನ್ನು ರಚಿಸಿ",
      instantOtp: "ತಕ್ಷಣದ OTP ಪ್ರವೇಶ",
      email: "ಇಮೇಲ್",
      password: "ಪಾಸ್‌ವರ್ಡ್",
      fullName: "ಪೂರ್ಣ ಹೆಸರು",
      mobileNumber: "ಮೊಬೈಲ್ ಸಂಖ್ಯೆ",
      enterMobile: "10 ಅಂಕಿಯ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ",
      sendOtp: "OTP ಪರಿಶೀಲನೆಯನ್ನು ಕಳುಹಿಸಿ",
      enterOtp: "6 ಅಂಕಿಯ OTP ನಮೂದಿಸಿ",
      verifyOtp: "OTP ಕೋಡ್ ಪರಿಶೀಲಿಸಿ",
      pleaseWait: "ದಯವಿಟ್ಟು ನಿರೀಕ್ಷಿಸಿ...",
      sending: "ಕಳುಹಿಸಲಾಗುತ್ತಿದೆ...",
      verifying: "ಪರಿಶೀಲಿಸಲಾಗುತ್ತಿದೆ...",
      changeNumber: "ಸಂಖ್ಯೆಯನ್ನು ಬದಲಾಯಿಸಿ",
    },
    sidebar: {
      newChat: "ಹೊಸ ಚಾಟ್",
      recent: "ಇತ್ತೀಚಿನ",
      noChats: "ಇನ್ನೂ ಯಾವುದೇ ಚಾಟ್‌ಗಳಿಲ್ಲ",
      signInHistory: "ಇತಿಹಾಸವನ್ನು ನೋಡಲು ಸೈನ್ ಇನ್ ಮಾಡಿ",
      loading: "ಲೋಡ್ ಆಗುತ್ತಿದೆ...",
      untitled: "ಶೀರ್ಷಿಕೆಯಿಲ್ಲದ",
      credits: "ಕ್ರೆಡಿಟ್‌ಗಳು",
      upgrade: "ಪ್ಲಾನ್ ಅಪ್‌ಗ್ರೇಡ್ ಮಾಡಿ",
      myAccount: "ನನ್ನ ಖಾತೆ",
      logout: "ಲಾಗ್ ಔಟ್",
    },
    footer: {
      privacy: "ಗೌಪ್ಯತೆ",
      terms: "ನಿಯಮಗಳು",
      usage: "ನೀತಿ",
    },
    authModal: {
      loginTitle: "ಲಾಗಿನ್ ಮಾಡಿ",
      signupTitle: "ಸೈನ್ ಅಪ್ ಮಾಡಿ",
      loginSub: "GR ಸಹಾಯಕ ಪ್ರವೇಶಿಸಿ",
      signupSub: "ಹೊಸ ಖಾತೆ ರಚಿಸಿ",
      cancel: "ರದ್ದುಮಾಡಿ",
      noAccount: "ಖಾತೆ ಇಲ್ಲವೇ?",
      hasAccount: "ಈಗಾಗಲೇ ಖಾತೆ ಇದೆಯೇ?",
    },
    account: {
      myAccount: "ನನ್ನ ಖಾತೆ",
      editProfileTitle: "ಪ್ರೊಫೈಲ್ ಸಂಪಾದಿಸಿ",
      user: "ಬಳಕೆದಾರ",
      admin: "ಅಡ್ಮಿನ್",
      active: "ಸಕ್ರಿಯ",
      blocked: "ಬ್ಲಾಕ್ ಮಾಡಲಾಗಿದೆ",
      joinedOn: "ಸೇರಿದ ದಿನಾಂಕ",
      editProfile: "ಪ್ರೊಫೈಲ್ ಸಂಪಾದಿಸಿ",
      credits: "ಕ್ರೆಡಿಟ್‌ಗಳು",
      balance: "ಬ್ಯಾಲೆನ್ಸ್",
      totalPurchased: "ಒಟ್ಟು ಖರೀದಿಸಿದ್ದು",
      used: "ಬಳಸಿದ್ದು",
      creditUsage: "ಕ್ರೆಡಿಟ್ ಬಳಕೆ",
      deptPref: "ಇಲಾಖೆ ಆದ್ಯತೆಗಳು",
      noDept: "ಯಾವುದೇ ಇಲಾಖೆಯನ್ನು ಆಯ್ಕೆ ಮಾಡಲಾಗಿಲ್ಲ.",
      selectDept: "+ ಇಲಾಖೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ",
      changeDept: "ಇಲಾಖೆಯನ್ನು ಬದಲಾಯಿಸಿ",
      actions: "ಕ್ರಿಯೆಗಳು",
      billingHistory: "ಬಿಲ್ಲಿಂಗ್ ಇತಿಹಾಸ",
      billingDesc: "ನಿಮ್ಮ ಎಲ್ಲಾ ಖರೀದಿಗಳ ಇತಿಹಾಸ",
      upgradePlan: "ಪ್ಲಾನ್ ಅಪ್‌ಗ್ರೇಡ್ ಮಾಡಿ",
      upgradeDesc: "ಹೆಚ್ಚಿನ ಕ್ರೆಡಿಟ್‌ಗಳನ್ನು ಪಡೆಯಿರಿ",
      loadingProfile: "ಪ್ರೊಫೈಲ್ ಲೋಡ್ ಆಗುತ್ತಿದೆ…",
      unableProfile: "ಪ್ರೊಫೈಲ್ ಲೋಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಿಲ್ಲ.",
      back: "ಹಿಂತಿರುಗಿ",
      saveChanges: "ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ",
      saving: "ಉಳಿಸಲಾಗುತ್ತಿದೆ…",
      profileUpdated: "ಪ್ರೊಫೈಲ್ ಯಶಸ್ವಿಯಾಗಿ ನವೀಕರಿಸಲಾಗಿದೆ!",
      nameLabel: "ಪೂರ್ಣ ಹೆಸರು",
      yourName: "ನಿಮ್ಮ ಹೆಸರು",
    },
    subscription: {
      title: "ಚಂದಾದಾರಿಕೆ ಯೋಜನೆಗಳು",
      subtitle: "ನಿಮ್ಮ ಅಗತ್ಯಗಳಿಗೆ ಸೂಕ್ತವಾದ ಯೋಜನೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ.",
      monthly: "ಮಾಸಿಕ",
      annual: "ವಾರ್ಷಿಕ",
      choosePlan: "ಯೋಜನೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ",
      currentBalance: "ನಿಮ್ಮ ಪ್ರಸ್ತುತ ಬ್ಯಾಲೆನ್ಸ್",
      mostPopular: "ಅತ್ಯಂತ ಜನಪ್ರಿಯ",
    },
  },
  ml: {
    navbar: {
      brandMain: "ഗവൺമെന്റ് അസിസ്റ്റന്റ്",
      brandSub: "മഹാരാഷ്ട്ര ഗവൺമെന്റ് ഡിജിറ്റൽ ആശയവിനിമയം",
    },
    login: {
      appTitle: "GRGPT & ടെൻഡർ അസിസ്റ്റന്റ്",
      signIn: "സൈൻ ഇൻ",
      signUp: "സൈൻ അപ്",
      otpLogin: "OTP ലോഗിൻ / സൈൻ അപ്",
      welcomeBack: "വീണ്ടും സ്വാഗതം",
      createAccount: "നിങ്ങളുടെ അക്കൗണ്ട് സൃഷ്ടിക്കുക",
      instantOtp: "തൽക്ഷണ OTP ആക്സസ്",
      email: "ഇമെയിൽ",
      password: "പാസ്‌വേഡ്",
      fullName: "പൂർണ്ണമായ പേര്",
      mobileNumber: "മൊബൈൽ നമ്പർ",
      enterMobile: "10 അക്ക നമ്പർ നൽകുക",
      sendOtp: "OTP പരിശോധന അയയ്ക്കുക",
      enterOtp: "6 അക്ക OTP നൽകുക",
      verifyOtp: "OTP കോഡ് പരിശോധിക്കുക",
      pleaseWait: "ദയവായി കാത്തിരിക്കുക...",
      sending: "അയക്കുന്നു...",
      verifying: "പരിശോധിക്കുന്നു...",
      changeNumber: "നമ്പർ മാറ്റുക",
    },
    sidebar: {
      newChat: "പുതിയ ചാറ്റ്",
      recent: "സമീപകാല",
      noChats: "ചാറ്റുകൾ ഒന്നും ലഭ്യമല്ല",
      signInHistory: "ചരിത്രം കാണാൻ സൈൻ ഇൻ ചെയ്യുക",
      loading: "ലോഡ് ചെയ്യുന്നു...",
      untitled: "തലക്കെട്ടില്ലാത്തത്",
      credits: "ക്രഡിറ്റുകൾ",
      upgrade: "പ്ലാൻ അപ്ഗ്രേഡ് ചെയ്യുക",
      myAccount: "എന്റെ അക്കൗണ്ട്",
      logout: "ലോഗ് ഔട്ട്",
    },
    footer: {
      privacy: "സ്വകാര്യത",
      terms: "നിബന്ധനകൾ",
      usage: "നയം",
    },
    authModal: {
      loginTitle: "ലോഗിൻ ചെയ്യുക",
      signupTitle: "സൈൻ അപ് ചെയ്യുക",
      loginSub: "GR അസിസ്റ്റന്റ് ആക്സസ് ചെയ്യുക",
      signupSub: "പുതിയ അക്കൗണ്ട് സൃഷ്ടിക്കുക",
      cancel: "റദ്ദാക്കുക",
      noAccount: "അക്കൗണ്ട് ഇല്ലേ?",
      hasAccount: "ഇനിയും അക്കൗണ്ട് ഉണ്ടോ?",
    },
    account: {
      myAccount: "എന്റെ അക്കൗണ്ട്",
      editProfileTitle: "പ്രൊഫൈൽ എഡിറ്റ് ചെയ്യുക",
      user: "ഉപയോക്താവ്",
      admin: "അഡ്മിൻ",
      active: "ആക്റ്റീവ്",
      blocked: "ബ്ലോക്ക് ചെയ്തു",
      joinedOn: "ചേർന്ന തീയതി",
      editProfile: "പ്രൊഫൈൽ എഡിറ്റ് ചെയ്യുക",
      credits: "ക്രഡിറ്റുകൾ",
      balance: "ബാലൻസ്",
      totalPurchased: "ആകെ വാങ്ങിയത്",
      used: "ഉപയോഗിച്ചത്",
      creditUsage: "ക്രഡിറ്റ് ഉപയോഗം",
      deptPref: "വകുപ്പ് മുൻഗണനകൾ",
      noDept: "വകുപ്പുകളൊന്നും തിരഞ്ഞെടുത്തിട്ടില്ല.",
      selectDept: "+ വകുപ്പ് തിരഞ്ഞെടുക്കുക",
      changeDept: "വകുപ്പ് മാറ്റുക",
      actions: "നടപടികൾ",
      billingHistory: "ബില്ലിംഗ് ചരിത്രം",
      billingDesc: "നിങ്ങളുടെ എല്ലാ വാങ്ങലുകളുടെയും ചരിത്രം",
      upgradePlan: "പ്ലാൻ അപ്ഗ്രേഡ് ചെയ്യുക",
      upgradeDesc: "കൂടുതൽ ക്രഡിറ്റുകൾ നേടുക",
      loadingProfile: "പ്രൊഫൈൽ ലോഡ് ചെയ്യുന്നു…",
      unableProfile: "പ്രൊഫൈൽ ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല.",
      back: "പിന്നോട്ട്",
      saveChanges: "മാറ്റങ്ങൾ സംരക്ഷിക്കുക",
      saving: "സംരക്ഷിക്കുന്നു…",
      profileUpdated: "പ്രൊഫൈൽ വിജയകരമായി അപ്ഡേറ്റ് ചെയ്തു!",
      nameLabel: "പൂർണ്ണമായ പേര്",
      yourName: "നിങ്ങളുടെ പേര്",
    },
    subscription: {
      title: "സബ്സ്ക്രിപ്ഷൻ പ്ലാനുകൾ",
      subtitle: "നിങ്ങളുടെ ആവശ്യങ്ങൾക്ക് അനുയോജ്യമായ പ്ലാൻ തിരഞ്ഞെടുക്കുക.",
      monthly: "പ്രതിമാസം",
      annual: "പ്രതിവർഷം",
      choosePlan: "പ്ലാൻ തിരഞ്ഞെടുക്കുക",
      currentBalance: "നിങ്ങളുടെ നിലവിലെ ബാലൻസ്",
      mostPopular: "ഏറ്റവും ജനപ്രിയമായത്",
    },
  },
  pa: {
    navbar: {
      brandMain: "ਸਰਕਾਰੀ ਸਹਾਇਕ",
      brandSub: "ਮਹਾਰਾਸ਼ਟਰ ਸਰਕਾਰ ਡਿਜੀਟਲ ਸੰਚਾਰ",
    },
    login: {
      appTitle: "GRGPT ਅਤੇ ਟੈਂਡਰ ਸਹਾਇਕ",
      signIn: "ਸਾਈਨ ਇਨ",
      signUp: "ਸਾਈਨ ਅੱਪ",
      otpLogin: "OTP ਲੌਗਇਨ / ਸਾਈਨ ਅੱਪ",
      welcomeBack: "ਜੀ ਆਇਆਂ ਨੂੰ",
      createAccount: "ਆਪਣਾ ਖਾਤਾ ਬਣਾਓ",
      instantOtp: "ਤੁਰੰਤ OTP ਪਹੁੰਚ",
      email: "ਈਮੇਲ",
      password: "ਪਾਸਵਰਡ",
      fullName: "ਪੂਰਾ ਨਾਮ",
      mobileNumber: "ਮੋਬਾਈਲ ਨੰਬਰ",
      enterMobile: "10-ਅੰਕਾਂ ਦਾ ਨੰਬਰ ਦਰਜ ਕਰੋ",
      sendOtp: "OTP ਵੀਰੀਫਿਕੇਸ਼ਨ ਭੇਜੋ",
      enterOtp: "6-ਅੰਕਾਂ ਦਾ OTP ਦਰਜ ਕਰੋ",
      verifyOtp: "OTP ਕੋਡ ਵੀਰੀਫਾਈ ਕਰੋ",
      pleaseWait: "ਕ੍ਰਿਪਾ ਕਰਕੇ ਉਡੀਕ ਕਰੋ...",
      sending: "ਭੇਜਿਆ ਜਾ ਰਿਹਾ ਹੈ...",
      verifying: "ਜਾਂਚ ਕੀਤੀ ਜਾ ਰਹੀ ਹੈ...",
      changeNumber: "ਨੰਬਰ ਬਦਲੋ",
    },
    sidebar: {
      newChat: "ਨਵੀਂ ਚੈਟ",
      recent: "ਹਾਲ ਹੀ ਵਿੱਚ",
      noChats: "ਅਜੇ ਤੱਕ ਕੋਈ ਚੈਟ ਨਹੀਂ",
      signInHistory: "ਹਿਸਟਰੀ ਦੇਖਣ ਲਈ ਸਾਈਨ ਇਨ ਕਰੋ",
      loading: "ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ...",
      untitled: "ਬਿਨਾ ਸਿਰਲੇਖ",
      credits: "ਕਰੈਡਿਟ",
      upgrade: "ਪਲਾਨ ਅੱਪਗ੍ਰੇਡ ਕਰੋ",
      myAccount: "ਮੇਰਾ ਖਾਤਾ",
      logout: "ਲੌਗ ਆਉਟ",
    },
    footer: {
      privacy: "ਗੋਪਨੀਯਤਾ",
      terms: "ਸ਼ਰਤਾਂ",
      usage: "ਨੀਤੀ",
    },
    authModal: {
      loginTitle: "ਲੌਗਇਨ ਕਰੋ",
      signupTitle: "ਸਾਈਨ ਅੱਪ ਕਰੋ",
      loginSub: "GR ਸਹਾਇਕ ਵਿੱਚ ਲੌਗਇਨ ਕਰੋ",
      signupSub: "ਨਵਾਂ ਖਾਤਾ ਬਣਾਓ",
      cancel: "ਰੱਦ ਕਰੋ",
      noAccount: "ਖਾਤਾ ਨਹੀਂ ਹੈ?",
      hasAccount: "ਪਹਿਲਾਂ ਤੋਂ ਖਾਤਾ ਹੈ?",
    },
    account: {
      myAccount: "ਮੇਰਾ ਖਾਤਾ",
      editProfileTitle: "ਪ੍ਰੋਫਾਈਲ ਐਡਿਟ ਕਰੋ",
      user: "ਵਰਤੋਂਕਾਰ",
      admin: "ਐਡਮਿਨ",
      active: "ਐਕਟਿਵ",
      blocked: "ਬਲਾਕ ਕੀਤਾ ਗਿਆ",
      joinedOn: "ਸ਼ਾਮਲ ਹੋਣ ਦੀ ਮਿਤੀ",
      editProfile: "ਪ੍ਰੋਫਾਈਲ ਐਡਿਟ ਕਰੋ",
      credits: "ਕਰੈਡਿਟ",
      balance: "ਬਕਾਇਆ",
      totalPurchased: "ਕੁੱਲ ਖਰੀਦਿਆ ਗਿਆ",
      used: "ਵਰਤਿਆ ਗਿਆ",
      creditUsage: "ਕਰੈਡਿਟ ਵਰਤੋਂ",
      deptPref: "ਵਿਭਾਗ ਤਰਜੀਹਾਂ",
      noDept: "ਕੋਈ ਵਿਭਾਗ ਚੁਣਿਆ ਨਹੀਂ ਗਿਆ।",
      selectDept: "+ ਵਿਭਾਗ ਚੁਣੋ",
      changeDept: "ਵਿਭਾਗ ਬਦਲੋ",
      actions: "ਕਾਰਵਾਈਆਂ",
      billingHistory: "ਬਿਲਿੰਗ ਇਤਿਹਾਸ",
      billingDesc: "ਤੁਹਾਡੀਆਂ ਸਾਰੀਆਂ ਖਰੀਦਾਂ ਦਾ ਇਤਿਹਾਸ",
      upgradePlan: "ਪਲਾਨ ਅੱਪਗ੍ਰੇਡ ਕਰੋ",
      upgradeDesc: "ਹੋਰ ਕਰੈਡਿਟ ਪ੍ਰਾਪਤ ਕਰੋ",
      loadingProfile: "ਪ੍ਰੋਫਾਈਲ ਲੋਡ ਹੋ ਰਹੀ ਹੈ…",
      unableProfile: "ਪ੍ਰੋਫਾਈਲ ਲੋਡ ਕਰਨ ਵਿੱਚ ਅਸਮਰੱਥ।",
      back: "ਵਾਪਸ",
      saveChanges: "ਤਬਦੀਲੀਆਂ ਸੰਭਾਲੋ",
      saving: "ਸੰਭਾਲਿਆ ਜਾ ਰਿਹਾ ਹੈ…",
      profileUpdated: "ਪ੍ਰੋਫਾਈਲ ਸਫਲਤਾਪੂਰਵਕ ਅੱਪਡੇਟ ਕੀਤੀ ਗਈ!",
      nameLabel: "ਪੂਰਾ ਨਾਮ",
      yourName: "ਤੁਹਾਡਾ ਨਾਮ",
    },
    subscription: {
      title: "ਸਬਸਕ੍ਰਿਪਸ਼ਨ ਪਲਾਨ",
      subtitle: "ਆਪਣੀ ਲੋੜ ਮੁਤਾਬਕ ਸਹੀ ਪਲਾਨ ਚੁਣੋ।",
      monthly: "ਮਹੀਨਾਵਾਰ",
      annual: "ਸਾਲਾਨਾ",
      choosePlan: "ਪਲਾਨ ਚੁਣੋ",
      currentBalance: "ਤੁਹਾਡਾ ਮੌਜੂਦਾ ਬਕਾਇਆ",
      mostPopular: "ਸਭ ਤੋਂ ਪ੍ਰਸਿੱਧ",
    },
  },
  ur: {
    navbar: {
      brandMain: "سستی معاون",
      brandSub: "مہاراشٹرا حکومت کی ڈیجیٹل مواصلات",
    },
    login: {
      appTitle: "GRGPT اور ٹینڈر اسسٹنٹ",
      signIn: "سائن ان",
      signUp: "سائن اپ",
      otpLogin: "OTP لاگ ان / سائن اپ",
      welcomeBack: "خوش آمدید",
      createAccount: "اپنا اکاؤنٹ بنائیں",
      instantOtp: "فوری OTP رسائی",
      email: "ای میل",
      password: "پاس ورڈ",
      fullName: "مکمل نام",
      mobileNumber: "موبائل نمبر",
      enterMobile: "10 ہندسوں کا نمبر درج کریں",
      sendOtp: "OTP کی تصدیق بھیجیں",
      enterOtp: "6 ہندسوں کا OTP درج کریں",
      verifyOtp: "OTP کوڈ کی تصدیق کریں",
      pleaseWait: "براہ کرم انتظار کریں...",
      sending: "بھیجا جا رہا ہے...",
      verifying: "تصدیق کی جا رہی ہے...",
      changeNumber: "نمبر تبدیل کریں",
    },
    sidebar: {
      newChat: "نئی بات چیت",
      recent: "حالیہ",
      noChats: "ابھی تک کوئی چیٹ نہیں ہے",
      signInHistory: "ہسٹری دیکھنے کے لیے سائن ان کریں",
      loading: "لوڈ ہو رہا ہے...",
      untitled: "بغیر عنوان",
      credits: "کریڈٹ",
      upgrade: "پلان اپ گریڈ کریں",
      myAccount: "میرا اکاؤنٹ",
      logout: "لاگ آؤٹ",
    },
    footer: {
      privacy: "رازداری",
      terms: "شرائط",
      usage: "پالیسی",
    },
    authModal: {
      loginTitle: "لاگ ان کریں",
      signupTitle: "سائن اپ کریں",
      loginSub: "GR اسسٹنٹ تک رسائی حاصل کریں",
      signupSub: "نیا اکاؤنٹ بنائیں",
      cancel: "منسوخ کریں",
      noAccount: "اکاؤنٹ نہیں ہے؟",
      hasAccount: "پہلے سے اکاؤنٹ موجود ہے؟",
    },
    account: {
      myAccount: "میرا اکاؤنٹ",
      editProfileTitle: "پروفائل میں ترمیم کریں",
      user: "صارف",
      admin: "ایڈمن",
      active: "فعال",
      blocked: "بلاک کر دیا گیا",
      joinedOn: "شمولیت کی تاریخ",
      editProfile: "پروفائل میں ترمیم کریں",
      credits: "کریڈٹ",
      balance: "بقیہ بیلنس",
      totalPurchased: "کل خریدا گیا",
      used: "استعمال شدہ",
      creditUsage: "کریڈٹ کا استعمال",
      deptPref: "محکمے کی ترجیحات",
      noDept: "کوئی محکمہ منتخب نہیں کیا گیا۔",
      selectDept: "+ محکمہ منتخب کریں",
      changeDept: "محکمہ تبدیل کریں",
      actions: "اقدامات",
      billingHistory: "بلنگ کی تاریخ",
      billingDesc: "آپ کی تمام خریداریوں کی تاریخ",
      upgradePlan: "پلان اپ گریڈ کریں",
      upgradeDesc: "مزید کریڈٹ حاصل کریں",
      loadingProfile: "پروفائل لوڈ ہو رہی ہے…",
      unableProfile: "پروفائل لوڈ کرنے میں ناکام۔",
      back: "پیچھے",
      saveChanges: "تبدیلیاں محفوظ کریں",
      saving: "محفوظ ہو رہا ہے…",
      profileUpdated: "پروفائل کامیابی کے ساتھ اپ ڈیٹ ہو گئی!",
      nameLabel: "مکمل نام",
      yourName: "آپ کا نام",
    },
    subscription: {
      title: "سبسکرپشن پلانز",
      subtitle: "اپنی ضروریات کے مطابق صحیح پلان کا انتخاب کریں۔",
      monthly: "ماہانہ",
      annual: "سالانہ",
      choosePlan: "پلان منتخب کریں",
      currentBalance: "آپ کا موجودہ بیلنس",
      mostPopular: "سب سے زیادہ مقبول",
    },
  },
  or: {
    navbar: {
      brandMain: "ସରକାରୀ ସହାୟକ",
      brandSub: "ମହାରାଷ୍ଟ୍ର ସରକାର ଡିଜିଟାଲ୍ ସଂଯୋଗ",
    },
    login: {
      appTitle: "GRGPT ଏବଂ ଟେଣ୍ଡର ସହାୟକ",
      signIn: "ସାଇନ୍ ଇନ୍",
      signUp: "ସାଇନ୍ ଅପ୍",
      otpLogin: "OTP ଲଗଇନ୍ / ସାଇନ୍ ଅପ୍",
      welcomeBack: "ସ୍ୱାଗତ",
      createAccount: "ଆପଣଙ୍କ ଆକାଉଣ୍ଟ୍ ସୃଷ୍ଟି କରନ୍ତୁ",
      instantOtp: "ତୁରନ୍ତ OTP ଆକ୍ସେସ୍",
      email: "ଇମେଲ୍",
      password: "ପାସୱାର୍ଡ",
      fullName: "ପୂରା ନାମ",
      mobileNumber: "ମୋବାଇଲ୍ ନମ୍ବର",
      enterMobile: "10 ଅଙ୍କ ବିଶିଷ୍ଟ ନମ୍ବର ପ୍ରବେଶ କରନ୍ତୁ",
      sendOtp: "OTP ଯାଞ୍ଚ ପଠାନ୍ତୁ",
      enterOtp: "6 ଅଙ୍କ ବିଶିଷ୍ଟ OTP ପ୍ରବେଶ କରନ୍ତୁ",
      verifyOtp: "OTP କୋଡ୍ ଯାଞ୍ଚ କରନ୍ତୁ",
      pleaseWait: "ଦୟାକରି ଅପେକ୍ଷା କରନ୍ତୁ...",
      sending: "ପଠାଯାଉଛି...",
      verifying: "ଯାଞ୍ଚ କରାଯାଉଛି...",
      changeNumber: "ନମ୍ବର ବଦଳାନ୍ତୁ",
    },
    sidebar: {
      newChat: "ନୂତନ ଚାଟ୍",
      recent: "ସମ୍ପ୍ରତି",
      noChats: "ଏପର୍ଯ୍ୟନ୍ତ କୌଣସି ଚାଟ୍ ନାହିଁ",
      signInHistory: "ଇତିହାସ ଦେଖିବା ପାଇଁ ସାଇନ୍ ଇନ୍ କରନ୍ତୁ",
      loading: "ଲୋଡ୍ ହେଉଛି...",
      untitled: "ଶୀର୍ଷକହୀନ",
      credits: "କ୍ରେଡିଟ୍",
      upgrade: "ପ୍ଲାନ୍ ଅପଗ୍ରେଡ୍ କରନ୍ତୁ",
      myAccount: "ମୋର ଆକାଉଣ୍ଟ୍",
      logout: "ଲଗ୍ ଆଉଟ୍",
    },
    footer: {
      privacy: "ଗୋପନୀୟତା",
      terms: "ସର୍ତ୍ତାବଳୀ",
      usage: "ନୀତି",
    },
    authModal: {
      loginTitle: "ଲଗଇନ୍ କରନ୍ତୁ",
      signupTitle: "ସାଇନ୍ ଅପ୍ କରନ୍ତୁ",
      loginSub: "GR ସହାୟକ ପ୍ରବେଶ କରନ୍ତୁ",
      signupSub: "ନୂତନ ଆକାଉଣ୍ଟ୍ ସୃଷ୍ଟି କରନ୍ତୁ",
      cancel: "ବାତିଲ୍ କରନ୍ତୁ",
      noAccount: "ଆକାଉଣ୍ଟ୍ ନାହିଁ?",
      hasAccount: "ପୂର୍ବରୁ ଆକାଉଣ୍ଟ୍ ଅଛି?",
    },
    account: {
      myAccount: "ମୋର ଆକାଉଣ୍ଟ୍",
      editProfileTitle: "ପ୍ରୋଫାଇଲ୍ ସମ୍ପାଦନା",
      user: "ବ୍ୟବହାରକାରୀ",
      admin: "ଆଡମିନ୍",
      active: "ସକ୍ରିୟ",
      blocked: "ବ୍ଲକ୍ ହୋଇଛି",
      joinedOn: "ଯୋଗଦାନ ତାରିଖ",
      editProfile: "ପ୍ରୋଫାଇଲ୍ ସମ୍ପାଦନା",
      credits: "କ୍ରେଡିଟ୍",
      balance: "ବ୍ୟାଲେନ୍ସ",
      totalPurchased: "ମୋଟ କିଣାଯାଇଥିବା",
      used: "ବ୍ୟବହୃତ",
      creditUsage: "କ୍ରେଡିଟ୍ ବ୍ୟବହାର",
      deptPref: "ବିଭାଗ ପସନ୍ଦ",
      noDept: "କୌଣସି ବିଭାଗ ଚୟନ କରାଯାଇନାହିଁ।",
      selectDept: "+ ବିଭାଗ ଚୟନ କରନ୍ତୁ",
      changeDept: "ବିଭାଗ ବଦଳାନ୍ତୁ",
      actions: "କାର୍ଯ୍ୟାନୁଷ୍ଠାନ",
      billingHistory: "ବିଲିଂ ଇତିହାସ",
      billingDesc: "ଆପଣଙ୍କ ସମସ୍ତ କ୍ରୟର ଇତିହାସ",
      upgradePlan: "ପ୍ଲାନ୍ ଅପଗ୍ରେଡ୍ କରନ୍ତୁ",
      upgradeDesc: "ଅଧିକ କ୍ରେଡିଟ୍ ପାଆନ୍ତୁ",
      loadingProfile: "ପ୍ରୋଫାଇଲ୍ ଲୋଡ୍ ହେଉଛି…",
      unableProfile: "ପ୍ରୋଫାଇଲ୍ ଲୋଡ୍ କରିବାରେ ଅସମର୍ଥ।",
      back: "ଫେରନ୍ତୁ",
      saveChanges: "ପରିବର୍ତ୍ତନ ସଂରକ୍ଷଣ କରନ୍ତୁ",
      saving: "ସଂରକ୍ଷଣ କରାଯାଉଛି…",
      profileUpdated: "ପ୍ରୋଫାଇଲ୍ ସଫଳତାର ସହିତ ଅପଡେଟ୍ ହୋଇଛି!",
      nameLabel: "ପୂରା ନାମ",
      yourName: "ଆପଣଙ୍କ ନାମ",
    },
    subscription: {
      title: "ସବସ୍କ୍ରିପସନ୍ ପ୍ଲାନ୍",
      subtitle: "ଆପଣଙ୍କ ଆବଶ୍ୟକତା ଅନୁଯାୟୀ ସଠିକ୍ ପ୍ଲାନ୍ ବାଛନ୍ତୁ।",
      monthly: "ମାସିକ",
      annual: "ବାର୍ଷିକ",
      choosePlan: "ପ୍ଲାନ୍ ବାଛନ୍ତୁ",
      currentBalance: "ଆପଣଙ୍କ ବର୍ତ୍ତମାନର ବ୍ୟାଲେନ୍ସ",
      mostPopular: "ସବୁଠାରୁ ଲୋକପ୍ରିୟ",
    },
  },
};

export function getTranslation(lang, section, key) {
  const current = TRANSLATIONS[lang] || TRANSLATIONS.en;
  if (current && current[section] && current[section][key] !== undefined) {
    return current[section][key];
  }
  const fallback = TRANSLATIONS.en[section];
  if (fallback && fallback[key] !== undefined) {
    return fallback[key];
  }
  return key;
}

/**
 * Formats next credit renewal timestamp in a clear, user-readable way.
 * Respects local browser timezone.
 *
 * @param {string|number|Date} nextResetAt
 * @param {string|number|Date} lastDailyCreditReset
 * @returns {string|null} e.g. "Renews today at 11:00 PM", "Renews tomorrow at 12:00 AM", "Renews on 28 Sep at 2:30 PM"
 */
export function formatRenewalTime(nextResetAt, lastDailyCreditReset) {
  let targetDate = null;
  if (nextResetAt) {
    targetDate = new Date(nextResetAt);
  } else if (lastDailyCreditReset) {
    targetDate = new Date(new Date(lastDailyCreditReset).getTime() + 24 * 60 * 60 * 1000);
  }

  if (!targetDate || isNaN(targetDate.getTime())) {
    return null;
  }

  const now = new Date();
  const diffMs = targetDate.getTime() - now.getTime();

  if (diffMs <= 0) {
    return "Renewal due now";
  }

  const timeStr = targetDate.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const isToday =
    targetDate.getDate() === now.getDate() &&
    targetDate.getMonth() === now.getMonth() &&
    targetDate.getFullYear() === now.getFullYear();

  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const isTomorrow =
    targetDate.getDate() === tomorrow.getDate() &&
    targetDate.getMonth() === tomorrow.getMonth() &&
    targetDate.getFullYear() === tomorrow.getFullYear();

  if (isToday) {
    return `Renews today at ${timeStr}`;
  } else if (isTomorrow) {
    return `Renews tomorrow at ${timeStr}`;
  } else {
    const dateStr = targetDate.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
    });
    return `Renews on ${dateStr} at ${timeStr}`;
  }
}

